import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MessageSquareText, ChevronLeft, Send } from "lucide-react";
import { toast } from "sonner";

import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABEL = {
  bug: "Bug",
  confusing: "Confusing",
  suggestion: "Suggestion",
  praise: "Praise",
  other: "Other",
};

const STATUS_LABEL = {
  new: "New",
  in_review: "In review",
  planned: "Planned",
  resolved: "Resolved",
  wont_fix: "Won't fix",
};

function Thread({ replies }) {
  if (!replies || replies.length === 0) {
    return (
      <div className="text-xs italic text-muted-foreground mt-2" data-testid="my-feedback-no-replies">
        No replies yet. We&rsquo;ll notify you here as soon as we respond.
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-2" data-testid="my-feedback-thread">
      {replies.map((r) => {
        const fromAdmin = r.author_role === "admin";
        return (
          <div
            key={r.id}
            className={`rounded-lg border p-3 text-sm ${
              fromAdmin
                ? "border-[hsl(var(--sb-forest))]/30 bg-[hsl(var(--sb-forest))]/[0.06]"
                : "border-border bg-muted/40"
            }`}
            data-testid={`my-feedback-reply-${r.id}`}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] font-semibold">
                {fromAdmin ? "Last Bookmark" : "You"}
                {fromAdmin && r.author_display_name && (
                  <span className="text-muted-foreground font-normal"> · {r.author_display_name}</span>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {new Date(r.created_at).toLocaleString()}
              </div>
            </div>
            <div className="mt-1 whitespace-pre-wrap break-words">{r.body}</div>
          </div>
        );
      })}
    </div>
  );
}

function ReplyComposer({ onSend, sending }) {
  const [body, setBody] = useState("");
  return (
    <div className="mt-3">
      <label htmlFor="my-feedback-reply-body" className="text-xs uppercase tracking-wide text-muted-foreground">
        Reply
      </label>
      <textarea
        id="my-feedback-reply-body"
        rows={3}
        maxLength={4000}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Reply to Last Bookmark — we&rsquo;ll see it in the same thread."
        className="sb-input mt-1 resize-none"
        data-testid="my-feedback-reply-body"
      />
      <div className="mt-2 flex items-center justify-end">
        <button
          type="button"
          disabled={sending || !body.trim()}
          onClick={async () => {
            const ok = await onSend(body.trim());
            if (ok) setBody("");
          }}
          data-testid="my-feedback-reply-send"
          className="btn-primary inline-flex items-center gap-1"
        >
          <Send size={14} /> {sending ? "Sending…" : "Send reply"}
        </button>
      </div>
    </div>
  );
}

function FeedbackCard({ item, expanded, onToggle, onReply, sending }) {
  const unreadFromAdmin =
    (item.replies || []).length > 0 &&
    item.replies[item.replies.length - 1].author_role === "admin";
  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 shadow-sm"
      data-testid={`my-feedback-item-${item.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            {TYPE_LABEL[item.type] || "Feedback"} · {STATUS_LABEL[item.status] || item.status}
          </div>
          <div className="mt-0.5 text-sm text-foreground line-clamp-2">{item.description}</div>
        </div>
        <div className="text-[11px] text-muted-foreground whitespace-nowrap">
          {new Date(item.created_at).toLocaleDateString()}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          className="btn-ghost border text-xs"
          onClick={onToggle}
          data-testid={`my-feedback-toggle-${item.id}`}
        >
          {expanded ? "Hide replies" : `View replies (${(item.replies || []).length})`}
        </button>
        {unreadFromAdmin && !expanded && (
          <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[hsl(var(--sb-forest))] text-white">
            New reply
          </span>
        )}
      </div>
      {expanded && (
        <div className="mt-2 border-t border-border/60 pt-3">
          <Thread replies={item.replies} />
          <ReplyComposer
            sending={sending}
            onSend={(body) => onReply(item.id, body)}
          />
        </div>
      )}
    </div>
  );
}

export default function MyFeedback() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(routeId || null);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/feedback/mine");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // When the URL contains /my-feedback/:id, auto-expand that item and
  // scroll it into view so the notification link lands the user right
  // on the reply thread.
  useEffect(() => {
    if (!routeId) return;
    setExpandedId(routeId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="my-feedback-item-${routeId}"]`);
      if (el) el.scrollIntoView({ block: "center" });
    }, 300);
  }, [routeId, items.length]);

  const reply = async (id, body) => {
    setSending(true);
    try {
      const { data } = await api.post(`/feedback/${id}/reply`, { body });
      setItems((prev) => prev.map((f) => (f.id === id ? data : f)));
      toast.success("Reply sent");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSending(false);
    }
  };

  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/dashboard" className="inline-flex items-center gap-1 hover:underline">
            <ChevronLeft size={12} /> Back to dashboard
          </Link>
        </div>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
          <MessageSquareText size={24} className="text-[hsl(var(--sb-forest-2))]" />
          Your feedback
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every piece of feedback you&rsquo;ve sent us, with the reply thread underneath. When we respond, you&rsquo;ll get a notification and see the new reply here.
        </p>

        {loading && <div className="mt-6 text-sm text-muted-foreground">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="mt-8 rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="my-feedback-empty">
            You haven&rsquo;t sent any feedback yet. Tap the floating Feedback button anywhere to start.
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="my-feedback-list">
          {items.map((it) => (
            <FeedbackCard
              key={it.id}
              item={it}
              expanded={expandedId === it.id}
              onToggle={() => {
                const next = expandedId === it.id ? null : it.id;
                setExpandedId(next);
                if (next) navigate(`/my-feedback/${it.id}`, { replace: true });
                else navigate("/my-feedback", { replace: true });
              }}
              onReply={reply}
              sending={sending}
            />
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
